import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WorkshopSelectionSto, WorkshopSto } from '../models/account/workshop.sto';
import { IFeatureToggle } from '../models/feature-toggle.model';
import { AccountReducers, AccountSelectors } from '../state/account';
import { getFeatureToggles } from '../state/feature-toggle';
import * as FeatureToggleReducers from '../state/feature-toggle/feature-toggle.reducer';

@Injectable({
  providedIn: 'root',
})
export class AccessGuard {
  private accountStore = inject<Store<AccountReducers.AccountState>>(Store);
  private featureToggleStore = inject<Store<FeatureToggleReducers.State>>(Store);

  private isProduction: boolean = environment.production;

  private selectedWorkshops$: Observable<WorkshopSelectionSto[]>;
  private userRoles$: Observable<string[]> = this.accountStore.select(AccountSelectors.getUserAuthorization).pipe(
    filter(auth => !!auth),
    map(auth => auth.activeDirectoryRoles)
  );
  private featureToggles$: Observable<IFeatureToggle[]>;

  constructor() {
    this.selectedWorkshops$ = this.accountStore
      .select(AccountSelectors.getSelectedWorkshops)
      .pipe(filter((_selectedWorkshops: WorkshopSelectionSto[]) => !!_selectedWorkshops));
    this.featureToggles$ = this.featureToggleStore.select(getFeatureToggles);
  }

  private _userHasPermission(permission: string, selectedWorkshops: WorkshopSelectionSto[]): boolean {
    return selectedWorkshops?.filter(ws => ws.permissions.filter((p: string) => p === permission).length > 0).length > 0;
  }

  private _userHasOnePermissionOf(neededPermissions: string[], selectedWorkshops: WorkshopSelectionSto[]): boolean {
    return !neededPermissions?.length || neededPermissions?.filter(p => this._userHasPermission(p, selectedWorkshops)).length > 0;
  }

  private _userHasOneRoleOf(neededRoles: string[], userRoles: string[]): boolean {
    return !!neededRoles?.find(r => userRoles.includes(r));
  }

  private _selectedWorkshopsHaveLicense(license: string, selectedWorkshops: WorkshopSto[]): boolean {
    return selectedWorkshops?.filter((workshop: WorkshopSto) => workshop?.dsmLicense === license).length > 0;
  }

  private _selectedWorkshopsHaveOneLicenseOf(neededLicenses: string[], selectedWorkshops: WorkshopSto[]): boolean {
    return !neededLicenses?.length || neededLicenses?.filter(l => this._selectedWorkshopsHaveLicense(l, selectedWorkshops)).length > 0;
  }

  private _isVisibleInProduction(isProductionReady: boolean | null | undefined): boolean {
    return !this.isProduction || isProductionReady === null || isProductionReady === undefined || !!isProductionReady;
  }

  private _isFeatureToggleNeededAndProvided(
    featureToggleNeeded: string | null | undefined,
    _featureToggles: IFeatureToggle[] | null | undefined
  ): boolean {
    if (featureToggleNeeded === undefined || featureToggleNeeded === null || featureToggleNeeded === '') {
      return true;
    }

    return (
      _featureToggles !== undefined && //
      _featureToggles !== null &&
      _featureToggles.some(toggle => toggle.id === featureToggleNeeded && (!toggle.workshopIds || toggle.workshopIds.length === 0))
    );
  }

  public isAccessOk(
    neededLicenses: string[],
    neededPermissions: string[],
    negatedRoles: string[] | undefined,
    isProductionReady: boolean | null | undefined,
    featureToggleNeeded: string | null | undefined
  ): Observable<boolean> {
    return combineLatest([this.selectedWorkshops$, this.featureToggles$, this.userRoles$]).pipe(
      map(([_selectedWorkshops, _featureToggles, userRoles]) => {
        return (
          this._selectedWorkshopsHaveOneLicenseOf(
            neededLicenses,
            _selectedWorkshops?.map(w => w.workshop)
          ) &&
          this._userHasOnePermissionOf(neededPermissions, _selectedWorkshops) &&
          !(negatedRoles && this._userHasOneRoleOf(negatedRoles, userRoles)) &&
          this._isVisibleInProduction(isProductionReady) &&
          this._isFeatureToggleNeededAndProvided(featureToggleNeeded, _featureToggles)
        );
      }),
      catchError(error => {
        console.log('access guard encountered an error, denying access.', error);
        return of(false);
      })
    );
  }

  canActivate(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): Observable<boolean> {
    const dsmLicenseNeeded: string[] = route?.data?.dsmLicenseNeeded;
    const permissionNeeded: string[] = route?.data?.permissionNeeded;

    return this.isAccessOk(dsmLicenseNeeded, permissionNeeded, undefined, route?.data?.isProductionReady, route?.data?.featureToggleNeeded);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(childRoute, state);
  }
}
